import type { Channel, Attachment, Message, Project, Reminder, Schedule, Todo } from "./types";

export type OfflineSnapshot = {
  userId: string; channelId: string; savedAt: number; channels: Channel[]; messages: Message[]; attachments: Attachment[];
  todos: Todo[]; reminders: Reminder[]; schedules: Schedule[]; projects: Project[]; lastSyncAt?: string;
};
const DB = "mindback-offline";
const STORE = "snapshots";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const key = (userId: string, channelId: string) => `${userId}:${channelId}`;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "key" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
function request<T>(db: IDBDatabase, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const operation = action(transaction.objectStore(STORE));
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(operation.error);
  });
}
export async function saveOfflineSnapshot(snapshot: OfflineSnapshot) {
  if (!globalThis.indexedDB) return;
  const db = await openDb();
  await request(db, "readwrite", (store) => store.put({ ...snapshot, key: key(snapshot.userId, snapshot.channelId) }));
  db.close();
}
export async function loadOfflineSnapshot(userId: string, channelId: string): Promise<OfflineSnapshot | null> {
  if (!globalThis.indexedDB) return null;
  const db = await openDb();
  const result = await request(db, "readonly", (store) => store.get(key(userId, channelId))) as (OfflineSnapshot & { key: string }) | undefined;
  db.close();
  if (!result || Date.now() - result.savedAt > MAX_AGE) return null;
  const { key: _key, ...snapshot } = result;
  return snapshot;
}
export async function clearOfflineSnapshots(userId: string) {
  if (!globalThis.indexedDB) return;
  const db = await openDb();
  const rows = await request(db, "readonly", (store) => store.getAll()) as Array<{ key: string; userId: string }>;
  await Promise.all(rows.filter((row) => row.userId === userId).map((row) => request(db, "readwrite", (store) => store.delete(row.key))));
  db.close();
}
